import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { Reservation, Table } from '../../types';
import {
  evaluatePartyTableAssignment,
  getConflictingReservationsForTables,
  getMaxSingleTableCapacity,
  getTablesFreeForSlot,
  isAdjacentTableCluster,
  RESERVATION_TABLE_BUFFER_MINUTES,
  suggestJoinCombinations,
  suggestTablesToJoin,
  TABLE_JOIN_ADJACENCY_PCT,
  tableFloorDistance,
} from '../../lib/reservationCapacity';
import { useSoftKeyboardField } from '../../hooks/useSoftKeyboardField';

interface PosGeneralReservationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (reservation: Omit<Reservation, 'id' | 'createdAt' | 'updatedAt'>) => void | Promise<void>;
  reservation?: Reservation | null;
  tables: Table[];
  reservations: Reservation[];
}

function coercePartySize(raw: string, fallback = 2): number {
  const n = parseInt(raw, 10);
  if (!Number.isFinite(n) || n < 1) return fallback;
  return Math.min(99, n);
}

/**
 * Thin POS modal for general (non-event) bookings — no event/slot fields.
 */
const PosGeneralReservationModal: React.FC<PosGeneralReservationModalProps> = ({
  isOpen,
  onClose,
  onSave,
  reservation,
  tables,
  reservations,
}) => {
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [partySize, setPartySize] = useState(2);
  const [partySizeInput, setPartySizeInput] = useState('2');
  const [reservationDate, setReservationDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [reservationTime, setReservationTime] = useState('18:00');
  const [tableId, setTableId] = useState(0);
  const [assignedTableIds, setAssignedTableIds] = useState<number[]>([]);
  /** Tables staff unchecked from a suggestion — used to surface other nearby pockets. */
  const [deselectedNearIds, setDeselectedNearIds] = useState<number[]>([]);
  const [notes, setNotes] = useState('');
  const [status, setStatus] = useState<Reservation['status']>('confirmed');
  const [saving, setSaving] = useState(false);
  /** Skip auto-suggest overwrite right after hydrating an edit. */
  const skipNextSuggestRef = useRef(false);

  const partySizeKeyboard = useSoftKeyboardField({
    id: 'pos-reservation-party-size',
    type: 'numeric',
    label: 'Party size',
    value: partySizeInput,
    onChange: (v) => {
      if (!(v === '' || /^\d{1,2}$/.test(v))) return;
      setPartySizeInput(v);
      if (v === '') return;
      const n = coercePartySize(v, partySize);
      setPartySize(n);
    },
    manualValidator: (v) => v === '' || /^\d{1,2}$/.test(v),
  });

  const commitPartySizeInput = () => {
    const n = coercePartySize(partySizeInput, 1);
    setPartySize(n);
    setPartySizeInput(String(n));
  };

  useEffect(() => {
    if (!isOpen) return;
    if (reservation) {
      const size = Math.max(1, reservation.partySize || 2);
      setCustomerName(reservation.customerName);
      setCustomerPhone(reservation.customerPhone ?? '');
      setPartySize(size);
      setPartySizeInput(String(size));
      setReservationDate(reservation.reservationDate);
      setReservationTime(reservation.reservationTime);
      setTableId(reservation.tableId ?? 0);
      setAssignedTableIds(
        reservation.assignedTableIds?.length
          ? reservation.assignedTableIds.map(Number)
          : reservation.tableId
            ? [Number(reservation.tableId)]
            : [],
      );
      setDeselectedNearIds([]);
      setNotes(reservation.notes || reservation.specialRequests || '');
      setStatus(reservation.status);
      skipNextSuggestRef.current = true;
    } else {
      setCustomerName('');
      setCustomerPhone('');
      setPartySize(2);
      setPartySizeInput('2');
      setReservationDate(new Date().toISOString().split('T')[0]);
      setReservationTime('18:00');
      setTableId(0);
      setAssignedTableIds([]);
      setDeselectedNearIds([]);
      setNotes('');
      setStatus('confirmed');
      skipNextSuggestRef.current = false;
    }
  }, [isOpen, reservation]);

  const maxSingleCapacity = useMemo(() => getMaxSingleTableCapacity(tables), [tables]);
  const freeForSlot = useMemo(
    () =>
      getTablesFreeForSlot(
        tables,
        reservations,
        reservationDate,
        reservationTime,
        reservation?.id,
        RESERVATION_TABLE_BUFFER_MINUTES,
      ),
    [tables, reservations, reservationDate, reservationTime, reservation?.id],
  );

  const needsJoin = partySize > maxSingleCapacity;
  const assignment = useMemo(
    () => evaluatePartyTableAssignment(freeForSlot, partySize, maxSingleCapacity),
    [freeForSlot, partySize, maxSingleCapacity],
  );

  const singleTableOptions = useMemo(() => {
    const fitting = freeForSlot.filter(
      (t) => t.capacity >= partySize || Number(t.id) === Number(tableId),
    );
    return fitting;
  }, [freeForSlot, partySize, tableId]);

  // Auto-suggest join set when party needs multiple tables.
  useEffect(() => {
    if (!isOpen || !needsJoin) return;
    if (skipNextSuggestRef.current) {
      skipNextSuggestRef.current = false;
      return;
    }
    const suggested = suggestTablesToJoin(freeForSlot, partySize);
    setAssignedTableIds(suggested.map((t) => Number(t.id)));
    setTableId(suggested[0] ? Number(suggested[0].id) : 0);
    setDeselectedNearIds([]);
  }, [isOpen, needsJoin, partySize, reservationDate, reservationTime, freeForSlot]);

  // Clear single-table pick when it no longer fits.
  useEffect(() => {
    if (needsJoin) return;
    if (!tableId) return;
    const t = tables.find((x) => Number(x.id) === Number(tableId));
    if (t && t.capacity < partySize) {
      setTableId(0);
    }
    setAssignedTableIds([]);
    setDeselectedNearIds([]);
  }, [needsJoin, partySize, tableId, tables]);

  const selectedJoinTables = useMemo(
    () =>
      assignedTableIds
        .map((id) => tables.find((t) => Number(t.id) === Number(id)))
        .filter(Boolean) as Table[],
    [assignedTableIds, tables],
  );
  const selectedJoinCapacity = selectedJoinTables.reduce((sum, t) => sum + t.capacity, 0);
  const selectedJoinAdjacent = isAdjacentTableCluster(selectedJoinTables);
  const joinOk =
    needsJoin &&
    assignedTableIds.length >= 2 &&
    selectedJoinCapacity >= partySize;

  const excludeForAlternates = useMemo(
    () =>
      deselectedNearIds.filter((id) => !assignedTableIds.some((x) => Number(x) === Number(id))),
    [deselectedNearIds, assignedTableIds],
  );

  const joinCombinations = useMemo(() => {
    if (!needsJoin || assignment.mode === 'impossible') return [];
    return suggestJoinCombinations(freeForSlot, partySize, {
      mustIncludeTableIds: assignedTableIds,
      excludeTableIds: excludeForAlternates,
      limit: 5,
    });
  }, [needsJoin, assignment.mode, freeForSlot, partySize, assignedTableIds, excludeForAlternates]);

  const primaryCombo = joinCombinations[0] ?? null;
  const alternateCombos = joinCombinations.slice(1);

  const nearHighlightIds = useMemo(() => {
    const ids = new Set<number>();
    for (const combo of joinCombinations) {
      for (const t of combo.tables) ids.add(Number(t.id));
    }
    // Mark nearest free neighbors of the current pick so staff can see options like T10/T13
    // even before the join set is complete.
    if (selectedJoinTables.length > 0) {
      const selectedIds = new Set(selectedJoinTables.map((t) => Number(t.id)));
      const neighborLimit = TABLE_JOIN_ADJACENCY_PCT * 1.5;
      const neighbors = freeForSlot
        .filter((t) => !selectedIds.has(Number(t.id)))
        .map((t) => ({
          id: Number(t.id),
          dist: Math.min(...selectedJoinTables.map((s) => tableFloorDistance(t, s))),
        }))
        .filter((n) => n.dist <= neighborLimit)
        .sort((a, b) => a.dist - b.dist)
        .slice(0, 6);
      for (const n of neighbors) ids.add(n.id);
    }
    return ids;
  }, [joinCombinations, selectedJoinTables, freeForSlot]);

  const freeForSlotSorted = useMemo(() => {
    const anchor =
      selectedJoinTables[0] ?? primaryCombo?.tables[0] ?? assignment.tables[0] ?? null;
    if (!anchor) return freeForSlot;
    return [...freeForSlot].sort((a, b) => {
      // Always prefer distance to the current selection when staff have picked a seed table.
      const origin = selectedJoinTables.length > 0 ? selectedJoinTables : [anchor];
      const da = Math.min(...origin.map((s) => tableFloorDistance(a, s)));
      const db = Math.min(...origin.map((s) => tableFloorDistance(b, s)));
      const aNear = nearHighlightIds.has(Number(a.id)) ? 0 : 1;
      const bNear = nearHighlightIds.has(Number(b.id)) ? 0 : 1;
      if (aNear !== bNear) return aNear - bNear;
      if (da !== db) return da - db;
      return a.name.localeCompare(b.name) || Number(a.id) - Number(b.id);
    });
  }, [freeForSlot, selectedJoinTables, primaryCombo, assignment.tables, nearHighlightIds]);

  const conflictReservations = useMemo(() => {
    const ids =
      assignedTableIds.length > 0
        ? assignedTableIds
        : tableId
          ? [tableId]
          : [];
    if (ids.length === 0) return [];
    return getConflictingReservationsForTables(
      reservations,
      reservationDate,
      reservationTime,
      ids,
      { bufferMinutes: RESERVATION_TABLE_BUFFER_MINUTES, excludeReservationId: reservation?.id },
    );
  }, [
    assignedTableIds,
    tableId,
    reservations,
    reservationDate,
    reservationTime,
    reservation?.id,
  ]);

  if (!isOpen) return null;

  const applyJoinCombination = (ids: number[]) => {
    const next = ids.map(Number);
    setAssignedTableIds(next);
    setTableId(next[0] ?? 0);
    setDeselectedNearIds([]);
  };

  const toggleJoinTable = (id: number) => {
    setAssignedTableIds((prev) => {
      const has = prev.some((x) => Number(x) === Number(id));
      const next = has ? prev.filter((x) => Number(x) !== Number(id)) : [...prev, id];
      setTableId(next[0] ? Number(next[0]) : 0);
      if (has) {
        // Remember unchecked tables so other nearby pockets can be suggested without them.
        setDeselectedNearIds((d) =>
          d.some((x) => Number(x) === Number(id)) ? d : [...d, Number(id)],
        );
      } else {
        // Fresh pick — clear exclusions so nearest neighbors of this table can appear.
        setDeselectedNearIds([]);
      }
      return next;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    commitPartySizeInput();
    const size = coercePartySize(partySizeInput, partySize);
    if (!customerName.trim()) {
      alert('Guest name is required.');
      return;
    }

    if (conflictReservations.length > 0) {
      const lines = conflictReservations
        .map(
          (r) =>
            `• ${r.customerName} at ${r.reservationTime} (party ${r.partySize})`,
        )
        .join('\n');
      const ok = window.confirm(
        `These tables already have a booking within ${RESERVATION_TABLE_BUFFER_MINUTES} minutes:\n${lines}\n\nSave this reservation anyway?`,
      );
      if (!ok) return;
    }

    if (size > maxSingleCapacity) {
      if (assignedTableIds.length < 2 || selectedJoinCapacity < size) {
        alert(
          assignment.mode === 'impossible'
            ? 'Not enough free table capacity for this party at this date/time. Change the slot or reduce party size.'
            : 'Select enough tables to join so combined seats cover the party.',
        );
        return;
      }
      const joinNames = selectedJoinTables.map((t) => t.name).join(', ');
      const joinNote = `Tables to join: ${joinNames}`;
      const mergedNotes = notes.trim()
        ? notes.trim().includes('Tables to join:')
          ? notes.trim()
          : `${notes.trim()} · ${joinNote}`
        : joinNote;
      setSaving(true);
      try {
        await onSave({
          tableId: Number(assignedTableIds[0]),
          assignedTableIds: assignedTableIds.map(Number),
          requiresMerge: true,
          customerName: customerName.trim(),
          customerPhone: customerPhone.trim(),
          partySize: size,
          reservationDate,
          reservationTime,
          status,
          notes: mergedNotes,
          eventId: undefined,
        });
        onClose();
      } catch {
        // Parent shows alert
      } finally {
        setSaving(false);
      }
      return;
    }

    if (!tableId) {
      alert('Please select a table.');
      return;
    }
    setSaving(true);
    try {
      await onSave({
        tableId,
        assignedTableIds: undefined,
        requiresMerge: false,
        customerName: customerName.trim(),
        customerPhone: customerPhone.trim(),
        partySize: size,
        reservationDate,
        reservationTime,
        status,
        notes: notes.trim() || undefined,
        eventId: undefined,
      });
      onClose();
    } catch {
      // Parent shows alert
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        className="w-full max-w-md rounded-xl bg-white shadow-xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b px-4 py-3 sticky top-0 bg-white z-10">
          <h2 className="text-lg font-bold text-gray-900">
            {reservation ? 'Edit reservation' : 'New reservation'}
          </h2>
          <button type="button" onClick={onClose} className="text-gray-500 hover:text-gray-800 text-xl leading-none">
            ×
          </button>
        </div>
        <form onSubmit={(e) => void handleSubmit(e)} className="space-y-3 p-4">
          <label className="block text-sm">
            <span className="font-semibold text-gray-700">Guest name</span>
            <input
              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              required
            />
          </label>
          <label className="block text-sm">
            <span className="font-semibold text-gray-700">Phone (optional)</span>
            <input
              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
              value={customerPhone}
              onChange={(e) => setCustomerPhone(e.target.value)}
            />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block text-sm">
              <span className="font-semibold text-gray-700">Party size</span>
              <input
                type="text"
                inputMode="numeric"
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                value={partySizeInput}
                data-soft-keyboard-handled="true"
                readOnly={partySizeKeyboard.readOnly}
                onFocus={partySizeKeyboard.onFocus}
                onChange={partySizeKeyboard.onChange}
                onBlur={commitPartySizeInput}
              />
            </label>
            <label className="block text-sm">
              <span className="font-semibold text-gray-700">Status</span>
              <select
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                value={status}
                onChange={(e) => setStatus(e.target.value as Reservation['status'])}
              >
                <option value="pending">Pending</option>
                <option value="confirmed">Confirmed</option>
                <option value="seated">Seated</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="block text-sm">
              <span className="font-semibold text-gray-700">Date</span>
              <input
                type="date"
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                value={reservationDate}
                onChange={(e) => setReservationDate(e.target.value)}
                required
              />
            </label>
            <label className="block text-sm">
              <span className="font-semibold text-gray-700">Time</span>
              <input
                type="time"
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                value={reservationTime}
                onChange={(e) => setReservationTime(e.target.value)}
                required
              />
            </label>
          </div>

          <p className="text-xs text-gray-500">
            Tables booked within {RESERVATION_TABLE_BUFFER_MINUTES} minutes of this time are hidden from
            suggestions so overlapping parties are not double-booked.
          </p>
          {conflictReservations.length > 0 && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-950">
              <p className="font-semibold">Table conflict within {RESERVATION_TABLE_BUFFER_MINUTES} minutes</p>
              <ul className="mt-1 list-disc pl-5 text-xs space-y-0.5">
                {conflictReservations.map((r) => (
                  <li key={r.id}>
                    {r.customerName} at {r.reservationTime} · party {r.partySize}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {!needsJoin ? (
            <label className="block text-sm">
              <span className="font-semibold text-gray-700">Table</span>
              <select
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                value={tableId || ''}
                onChange={(e) => setTableId(Number(e.target.value))}
                required
              >
                <option value="">Select table…</option>
                {singleTableOptions.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name} (seats {t.capacity})
                  </option>
                ))}
                {tableId > 0 && !singleTableOptions.some((t) => Number(t.id) === Number(tableId)) && (
                  <option value={tableId}>
                    {tables.find((t) => Number(t.id) === Number(tableId))?.name ?? `Table ${tableId}`} (current)
                  </option>
                )}
              </select>
            </label>
          ) : (
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 space-y-2">
              <p className="text-sm font-semibold text-blue-900">
                Party of {partySize} needs joined tables
              </p>
              <p className="text-xs text-blue-800">
                Largest single table seats {maxSingleCapacity}. Suggestions prefer tables next to each
                other on the floor plan (not capacity alone). Combined seats must cover the party.
              </p>
              {assignment.mode === 'impossible' ? (
                <p className="text-sm text-amber-800 font-medium">
                  Not enough free capacity for this date/time. Change the slot or reduce party size.
                </p>
              ) : (
                <>
                  {primaryCombo && primaryCombo.tables.length >= 2 && (
                    <p className="text-xs text-blue-900">
                      {assignedTableIds.length === 0 || !joinOk
                        ? 'Best nearby fit: '
                        : 'Current nearby set: '}
                      <span className="font-semibold">
                        {primaryCombo.tables.map((t) => t.name).join(' + ')}
                      </span>{' '}
                      ({primaryCombo.capacity} seats)
                      {!primaryCombo.adjacent && (
                        <span className="block mt-1 text-amber-800 font-medium">
                          This set is not fully adjacent — review the floor before confirming.
                        </span>
                      )}
                    </p>
                  )}
                  {alternateCombos.length > 0 && (
                    <div className="space-y-1.5">
                      <p className="text-xs font-semibold text-blue-900">Other nearby combinations</p>
                      <div className="flex flex-wrap gap-1.5">
                        {alternateCombos.map((combo) => {
                          const ids = combo.tables.map((t) => Number(t.id));
                          const label = combo.tables.map((t) => t.name).join(' + ');
                          return (
                            <button
                              key={ids.join('-')}
                              type="button"
                              onClick={() => applyJoinCombination(ids)}
                              className="rounded-md border border-blue-300 bg-white px-2 py-1 text-xs text-blue-900 hover:border-pink-400 hover:bg-pink-50"
                              title="Select this nearby group"
                            >
                              {label}{' '}
                              <span className="text-blue-600">({combo.capacity})</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  <div className="flex flex-wrap gap-2">
                    {freeForSlotSorted.map((t) => {
                      const checked = assignedTableIds.some((id) => Number(id) === Number(t.id));
                      const inPrimary = primaryCombo?.tables.some(
                        (s) => Number(s.id) === Number(t.id),
                      );
                      const inAnyNear = nearHighlightIds.has(Number(t.id));
                      return (
                        <label
                          key={t.id}
                          className={`flex items-center gap-2 cursor-pointer rounded-md border px-2 py-1.5 text-sm ${
                            checked
                              ? 'border-pink-400 bg-white text-gray-900'
                              : inPrimary
                                ? 'border-blue-400 bg-white text-gray-800'
                                : inAnyNear
                                  ? 'border-sky-300 bg-white text-gray-800'
                                  : 'border-blue-200 bg-white/70 text-gray-800'
                          }`}
                          title="Highlighted tables appear in nearby join options"
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleJoinTable(Number(t.id))}
                            className="rounded border-gray-300 text-pink-600 focus:ring-pink-500"
                          />
                          {t.name} ({t.capacity})
                          {inPrimary ? (
                            <span className="text-[10px] uppercase tracking-wide text-blue-600">near</span>
                          ) : inAnyNear ? (
                            <span className="text-[10px] uppercase tracking-wide text-sky-600">alt</span>
                          ) : null}
                        </label>
                      );
                    })}
                  </div>
                  {assignedTableIds.length > 0 && (
                    <>
                      <p
                        className={`text-sm font-medium ${
                          joinOk ? 'text-green-700' : 'text-amber-800'
                        }`}
                      >
                        Selected {selectedJoinCapacity} seats
                        {joinOk
                          ? ' — ready to save'
                          : ` — need ${Math.max(0, partySize - selectedJoinCapacity)} more`}
                        {assignedTableIds.length < 2 ? ' (pick at least 2 tables)' : ''}.
                      </p>
                      {!joinOk && primaryCombo && primaryCombo.tables.length > assignedTableIds.length && (
                        <button
                          type="button"
                          onClick={() =>
                            applyJoinCombination(primaryCombo.tables.map((t) => Number(t.id)))
                          }
                          className="text-xs font-semibold text-pink-700 hover:underline"
                        >
                          Use suggested completion:{' '}
                          {primaryCombo.tables.map((t) => t.name).join(' + ')}
                        </button>
                      )}
                      {assignedTableIds.length >= 2 && !selectedJoinAdjacent && (
                        <p className="text-xs text-amber-800 font-medium">
                          Selected tables are not next to each other on the floor — there may be
                          other tables between them.
                        </p>
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          )}

          <label className="block text-sm">
            <span className="font-semibold text-gray-700">Notes (optional)</span>
            <textarea
              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </label>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="rounded-lg px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100">
              Cancel
            </button>
            <button
              type="submit"
              disabled={
                saving ||
                (needsJoin && (assignment.mode === 'impossible' || !joinOk))
              }
              className="rounded-lg bg-pink-500 px-4 py-2 text-sm font-semibold text-white hover:bg-pink-600 disabled:opacity-60"
            >
              {saving ? 'Saving…' : needsJoin ? 'Save with joined tables' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PosGeneralReservationModal;
